import { useIntlLocale } from '../intl-locale';
import { personName } from './labels';
import type { ProjectTint } from './types';

/** Up to two letters for a project's mark: the first letters of its first two words. */
export function projectInitials(name: string) {
  const letters = name
    .trim()
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => [...word][0] ?? '')
    .join('');
  return letters.toLocaleUpperCase() || '·';
}

/** One letter for a person, as Chat's own avatars show it. */
export function personInitials(name: string) {
  return projectInitials(name).slice(0, 1);
}

/** A project's mark: its initials on its tint. Decorative; the name always sits beside it. */
export function ProjectMark({
  name,
  tint,
  size = 'md',
}: {
  name: string;
  tint: ProjectTint;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}) {
  return (
    <span className={`ws-project-mark ${size}`} data-tint={tint} aria-hidden="true">
      {projectInitials(name)}
    </span>
  );
}

/** Overlapping initials for the people in a project. */
export function ProjectFaces({
  people,
  max = 4,
  size = 'sm',
  label,
}: {
  people: { userId: string | null; name: string }[];
  max?: number;
  size?: 'sm' | 'lg';
  label?: string;
}) {
  const locale = useIntlLocale();
  const shown = people.slice(0, max);
  const more = people.length - shown.length;
  return (
    <span
      className={`ws-project-faces ${size}`}
      aria-label={label}
      role={label ? 'img' : undefined}
    >
      {shown.map((person, index) => (
        <span
          key={person.userId ?? `former-${index}`}
          className="ws-project-face"
          title={personName(person, locale)}
        >
          {personInitials(person.userId ? person.name : '')}
        </span>
      ))}
      {more > 0 && <span className="ws-project-face more">+{more}</span>}
    </span>
  );
}
